import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box } from '@mui/material';
import { makeRouteScenario } from 'src/utils/route';
import { getLocalBasket } from 'components/basket/localStore';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { LocationState } from 'components/location/model/Location';
import { OrderToTableLocationPicker } from 'components/location/OrderToTableLocationPicker';
import { OrderScenario } from 'components/order/model/Order';
import { getShouldUpdateGuestSession, resetShouldUpdateGuestSession } from 'components/user/localAuth';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { InnerPageLayout } from 'lib/InnerPageLayout';
import { InnerPageLayoutContent } from 'lib/InnerPageLayout/InnerPageLayoutContent';
import { ResponsiveLayout } from 'lib/Responsive';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { createGuestSession } from 'store/auth/authActions';
import { resetBasket, setBasket, setDeliveryLocation } from 'store/basket/basketActions';
import { ApplicationState } from 'store/store';
import { useMerchantLogo } from 'lib/appbar/useMerchantLogo';
import { LandingDesktop } from 'components/journey/ui/LandingPage/LandingDesktop';
import { isDefined, isEmptyString } from 'lib/typeInference';
import { useQuery } from 'src/hooks/useQuery';
import { ScenarioList } from 'components/journey/ui/LandingPage/ScenarioList';

export const JourneyLandingPage: React.FC = () => {
    const dispatch = useDispatch();
    const { push, replace } = useLocalHistory();
    const { locationId, merchantId } = useParams<LocationRouteParams>();
    const tableNumber = useQuery('tableNumber');

    const { deliveryLocation } = useSelector((state: ApplicationState) => state.basket);
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { currentLocation } = useLocationHelpers();
    const { isGuest } = useAuth();
    const renderMerchantTitle = useMerchantLogo();
    const showTableNumber = React.useMemo(
        () => (isDefined(tableNumber) && !isEmptyString(tableNumber)) || isDefined(deliveryLocation),
        [tableNumber, deliveryLocation]
    );
    const disableTableNumberEdit = React.useMemo(
        () => isDefined(deliveryLocation) && (!isDefined(tableNumber) || isEmptyString(tableNumber)),
        [deliveryLocation, tableNumber]
    );

    React.useEffect(() => {
        if (isGuest) {
            if (merchantId) {
                const shouldUpdateGuestSession = getShouldUpdateGuestSession(merchantId);
                if (shouldUpdateGuestSession) {
                    createGuestSession(dispatch).then(() => {
                        resetShouldUpdateGuestSession(merchantId);
                    });
                }
            }
        }
    }, [dispatch, isGuest, merchantId]);

    React.useEffect(() => {
        const localBasket = getLocalBasket();
        if (localBasket && localBasket.locationId === locationId) {
            setBasket({ deliveryLocation: localBasket.deliveryLocation, locationId })(dispatch);
        } else {
            resetBasket(dispatch);
        }
    }, [deliveryLocation, dispatch, locationId]);

    React.useEffect(() => {
        if (isDefined(tableNumber) && !isEmptyString(tableNumber)) {
            setDeliveryLocation(tableNumber)(dispatch);
        }
    }, [tableNumber, dispatch]);

    const handleScenarioChange = React.useCallback(
        (scenario: OrderScenario) => () => {
            setBasket({ locationId, deliveryLocation })(dispatch);
            switch (scenario) {
                case OrderScenario.ORDER_TO_TABLE:
                    if (merchantId) {
                        push(ROUTES.MENU, {
                            merchantId,
                            locationId: locationId || '',
                            scenario: makeRouteScenario(scenario)
                        });
                    }
                    break;
                case OrderScenario.DINE_IN:
                    if (merchantId) {
                        push(ROUTES.MENU, {
                            merchantId,
                            locationId: locationId || '',
                            scenario: makeRouteScenario(scenario)
                        });
                    }
                    break;
                case OrderScenario.PREORDER:
                    if (merchantId) {
                        push(
                            ROUTES.MENU,
                            {
                                merchantId,
                                locationId: locationId || '',
                                scenario: makeRouteScenario(scenario)
                            },
                            undefined,
                            {
                                preoderEntry: 'journey'
                            }
                        );
                    }
                    break;
                case OrderScenario.TABLE:
                    if (merchantId) {
                        push(
                            ROUTES.QUICKPAY.CHECKOUT,
                            {
                                merchantId,
                                locationId: locationId || '',
                                scenario: makeRouteScenario(scenario)
                            },
                            deliveryLocation ? `tableNumber=${deliveryLocation}` : undefined
                        );
                    }
                    break;
                default:
                    break;
            }
        },
        [deliveryLocation, dispatch, locationId, merchantId, push]
    );
    const menuLink = React.useMemo(
        () => settings?.payAtTable?.noTableCheckFoundUrl,
        [settings?.payAtTable?.noTableCheckFoundUrl]
    );
    const handleViewMenu = React.useCallback(() => window.open(menuLink, '_blank'), [menuLink]);

    const isLocationRetired = React.useMemo(
        () => !!currentLocation && currentLocation.state === LocationState.RETIRED,
        [currentLocation]
    );

    const handleTableChange = React.useCallback(
        ({ current }: { previous?: string; current: string }) => {
            replace('', {}, `tableNumber=${current}`);
        },
        [replace]
    );

    return (
        <ResponsiveLayout
            renderMobileView={
                <InnerPageLayout disableContentSpaces>
                    <MuiAppBar title={renderMerchantTitle} disableBack />
                    <OrderToTableLocationPicker
                        disableBottomSpace
                        pickerTitle="location"
                        locationId={locationId}
                        showTableNumber={showTableNumber}
                        onTableChange={handleTableChange}
                        disableEdit={disableTableNumberEdit}
                    />
                    <InnerPageLayoutContent>
                        <Box paddingBottom={2} marginTop={2} textAlign="center">
                            <ScenarioList
                                merchantId={merchantId}
                                currentLocation={currentLocation}
                                isLocationRetired={isLocationRetired}
                                menuLink={menuLink}
                                handleScenarioChange={handleScenarioChange}
                                handleViewMenu={handleViewMenu}
                            />
                        </Box>
                    </InnerPageLayoutContent>
                </InnerPageLayout>
            }
            renderDesktopView={
                <LandingDesktop
                    showTableNumber={showTableNumber}
                    disableTableNumberEdit={disableTableNumberEdit}
                    menuLink={menuLink}
                    isLocationRetired={isLocationRetired}
                    handleViewMenu={handleViewMenu}
                    handleScenarioChange={handleScenarioChange}
                    onTableChange={handleTableChange}
                />
            }
        />
    );
};
